import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 * 
 * This should be called before React app initialization in main.tsx
 * 
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry DSN (Data Source Name)
 * - VITE_SENTRY_ENVIRONMENT: Environment name (development, staging, production)
 * - VITE_SENTRY_RELEASE: Release version (optional)
 */
export const initSentry = () => {
  try {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.NODE_ENV || 'development';
    const release = import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.FIVE_APP_VERSION;

    // Only initialize Sentry if DSN is provided (silent when not configured)
    if (!dsn) return;

    Sentry.init({
      dsn,
      environment,
      release,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
      // Session Replay
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0, // Always capture replays on errors

      // Filter out development errors
      beforeSend(event) {
        // Don't send errors in development unless explicitly enabled
        if (environment === 'development' && !import.meta.env.VITE_SENTRY_ENABLE_DEV) {
          return null;
        }
        return event;
      },

      // Ignore common non-critical errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'atomicFindClose',
        // Network errors that are expected
        'NetworkError',
        'Network request failed',
        'Failed to fetch',
        // ResizeObserver errors (common and non-critical)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],

      // Filter out URLs that shouldn't be tracked
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Local development
        /^http:\/\/localhost/i,
        /^http:\/\/127\.0\.0\.1/i,
      ],
    });

    if (import.meta.env.DEV) {
      console.log('Sentry initialized for environment:', environment);
    }
  } catch {
    // Never let Sentry init block or break the app (e.g. CSP blocking ingest, or old mobile browsers)
  }
};

/**
 * Set user context for error tracking
 */
export const setSentryUser = (user: { id: string; username?: string; email?: string }) => {
  Sentry.setUser({
    id: user.id,
    username: user.username,
    email: user.email,
  });
};

/**
 * Clear user context (on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for debugging
 */
export const addSentryBreadcrumb = (
  message: string,
  category?: string,
  level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal',
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    data,
  });
};

/**
 * Capture exception manually
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Capture message manually
 */
export const captureMessage = (message: string, level?: Sentry.SeverityLevel) => {
  Sentry.captureMessage(message, level);
};







